import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { ColorToggles } from "@/components/common/ColorPips";
import { LAYOUTS, RARITIES } from "@/lib/mtg";

const SELECT_CLS = "field capitalize";

export function LibraryFilters({ params, setParam, reset, sets }) {
  const [q, setQ] = useState(params.q || "");
  useEffect(() => setQ(params.q || ""), [params.q]);
  useEffect(() => {
    const t = setTimeout(() => q !== (params.q || "") && setParam("q", q), 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const colors = (params.colors || "").split("").filter(Boolean);
  const colorValue = params.colorless ? [...colors, "C"] : colors;
  const onColors = (next) => {
    setParam("colorless", next.includes("C") ? "true" : "");
    setParam("colors", next.filter((c) => c !== "C").join(""));
  };

  return (
    <aside className="panel space-y-6 p-5" aria-label="Search filters" data-testid="library-filters">
      <div>
        <label className="field-label" htmlFor="card-search">Search</label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
          <input id="card-search" className="field pl-9" placeholder="Name, type or rules text" value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="card-search-input" />
        </div>
      </div>
      <div>
        <span className="field-label">Color identity</span>
        <ColorToggles value={colorValue} onChange={onColors} includeColorless testidPrefix="filter-color" />
        <select className={`${SELECT_CLS} mt-3`} value={params.color_mode || "include"} onChange={(e) => setParam("color_mode", e.target.value)}
          aria-label="Color match mode" data-testid="filter-color-mode">
          <option value="include">Includes any selected</option>
          <option value="all">Includes all selected</option>
          <option value="exact">Exactly these colors</option>
        </select>
      </div>
      <div>
        <label className="field-label" htmlFor="filter-type">Type line</label>
        <input id="filter-type" className="field" placeholder="e.g. Dragon, Instant" defaultValue={params.type || ""}
          onBlur={(e) => setParam("type", e.target.value)} onKeyDown={(e) => e.key === "Enter" && setParam("type", e.target.value)}
          data-testid="filter-type-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="field-label" htmlFor="filter-rarity">Rarity</label>
          <select id="filter-rarity" className={SELECT_CLS} value={params.rarity || ""} onChange={(e) => setParam("rarity", e.target.value)} data-testid="filter-rarity">
            <option value="">Any</option>
            {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="filter-layout">Layout</label>
          <select id="filter-layout" className={SELECT_CLS} value={params.layout || ""} onChange={(e) => setParam("layout", e.target.value)} data-testid="filter-layout">
            <option value="">Any</option>
            {LAYOUTS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <span className="field-label">Mana value</span>
        <div className="flex items-center gap-2">
          <input type="number" min="0" className="field" placeholder="Min" aria-label="Minimum mana value" value={params.mv_min || ""}
            onChange={(e) => setParam("mv_min", e.target.value)} data-testid="filter-mv-min" />
          <span className="text-slate-500">–</span>
          <input type="number" min="0" className="field" placeholder="Max" aria-label="Maximum mana value" value={params.mv_max || ""}
            onChange={(e) => setParam("mv_max", e.target.value)} data-testid="filter-mv-max" />
        </div>
      </div>
      {sets?.length > 0 && (
        <div>
          <label className="field-label" htmlFor="filter-set">Set</label>
          <select id="filter-set" className="field" value={params.set_id || ""} onChange={(e) => setParam("set_id", e.target.value)} data-testid="filter-set">
            <option value="">Any set</option>
            {sets.map((s) => <option key={s.set_id} value={s.set_id}>{`${s.code} — ${s.name}`}</option>)}
          </select>
        </div>
      )}
      <button className="btn-ghost w-full" onClick={reset} data-testid="filter-reset-button"><X className="h-4 w-4" />Clear filters</button>
    </aside>
  );
}
